import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { COUNTRY_DIAL_OPTIONS, flagImageUrl } from '../../utils/authPhone';

type Props = {
  value: string;
  onChange: (dialCode: string) => void;
  className?: string;
  /** Intégré dans un champ téléphone composite (sans fond ni bordure propres). */
  embedded?: boolean;
};

type MenuPosition = {
  top: number;
  left: number;
  width: number;
};

export default function CountryDialCodeSelect({ value, onChange, className = '', embedded = false }: Props) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const selected = COUNTRY_DIAL_OPTIONS.find((c) => c.dialCode === value) ?? COUNTRY_DIAL_OPTIONS[0];

  const updateMenuPosition = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    setMenuPosition({
      top: rect.top,
      left: rect.left,
      width: Math.max(rect.width, 176),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const menu =
    open && menuPosition
      ? createPortal(
          <ul
            ref={menuRef}
            className="fixed z-[200] max-h-64 overflow-y-auto overscroll-contain rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-1 shadow-lg"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              transform: 'translateY(calc(-100% - 6px))',
            }}
            role="listbox"
          >
            {COUNTRY_DIAL_OPTIONS.map((country) => {
              const active = country.code === selected.code && country.dialCode === selected.dialCode;
              return (
                <li key={`${country.code}-${country.dialCode}`}>
                  <button
                    type="button"
                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                      active ? 'bg-primary/10 text-primary' : 'text-on-surface hover:bg-primary/5'
                    }`}
                    onClick={() => {
                      onChange(country.dialCode);
                      setOpen(false);
                    }}
                  >
                    <img
                      src={flagImageUrl(country.code)}
                      alt={country.name}
                      className="h-[18px] w-6 rounded-sm object-cover shadow-sm"
                      loading="lazy"
                    />
                    <span className="font-semibold">{country.dialCode}</span>
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div className={`relative shrink-0 ${className}`} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className={
          embedded
            ? 'flex h-full items-center justify-between gap-0.5 py-2 pl-2.5 pr-1 text-sm text-on-surface focus:outline-none sm:py-2.5'
            : 'flex h-full min-h-[40px] items-center justify-between gap-1 rounded-lg bg-surface-container-high px-2 py-2 text-sm text-on-surface focus-glow focus:outline-none sm:min-h-[42px] sm:py-2.5'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((c) => !c)}
      >
        <span className="flex items-center gap-1.5">
          <img
            src={flagImageUrl(selected.code)}
            alt={selected.name}
            className="h-[18px] w-6 rounded-sm object-cover shadow-sm"
            loading="lazy"
          />
          <span className="text-sm font-semibold">{selected.dialCode}</span>
        </span>
        <LucideSymbolIcon name="expand_more" tone="muted" size={18} className="shrink-0" />
      </button>
      {menu}
    </div>
  );
}
